import { useQuery } from '@tanstack/react-query';
import {
  DrawerCloseButton,
  DrawerHeader,
  Heading,
  InlineStack,
  Paragraph,
  SkeletonText,
  Stack,
} from '@customerio/pluma-components/react';
import { dataIndexItemQueryOptions, toItemDetail } from '../item/hooks';
import { describeItem } from '../item/page-model';
import type { DataIndexItemAddress } from '../urls';

/**
 * The row being looked at, and what the list calls it — which for an event is
 * its ingested casing rather than the lowercased name its address carries.
 * Without one, the drawer is titled by the name.
 */
export type DataIndexDrawerAddress = DataIndexItemAddress & { displayName?: string };

type DrawerItemHeaderProps = {
  workspaceId: number;
  address: DataIndexDrawerAddress;
  /** The id the drawer names itself by. */
  titleId: string;
};

/**
 * The row's name and description, and the way out.
 *
 * Mounted through the read rather than suspended with it, as the page this
 * replaces keeps its header (`data-index-details-drawer/index.gts`): the
 * drawer aims its first focus at the close button a frame before it moves
 * there, and a button that was the skeleton's by then had gone, leaving the
 * focus on the row behind the panel. The name is the address's, so it is on
 * screen from the start and names the dialog while the rest is read. That
 * differs from Ember on purpose: Ember draws skeleton text where the name
 * goes until the row arrives, and this header shows the name from the start.
 */
export function DrawerItemHeader({ workspaceId, address, titleId }: DrawerItemHeaderProps) {
  return (
    <DrawerHeader display="block" position="sticky">
      <InlineStack gap="100">
        <Stack gap="100">
          <Heading id={titleId} level="2" truncate>
            {/* The row's name as the list shows it: an event keeps the
                casing it was sent with (`data-index-details-drawer/index.gts:443-445`). */}
            {address.displayName ?? address.name}
          </Heading>
          <ItemDescription workspaceId={workspaceId} address={address} />
        </Stack>
        <DrawerCloseButton />
      </InlineStack>
    </DrawerHeader>
  );
}

type ItemDescriptionProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
};

/**
 * Skeleton text until the body's read of the row arrives. A failed read of the
 * row shows nothing here: the body says what went wrong, and says it once.
 * The body's other reads, the tag list among them, do not reach the header, so
 * one of those failing leaves the description above the body's message.
 */
function ItemDescription({ workspaceId, address }: ItemDescriptionProps) {
  const { data, isPending } = useQuery({
    ...dataIndexItemQueryOptions(workspaceId, address),
    // Watched, never fetched: the body owns the read and does not retry one
    // that failed, so a header that fetched could answer where the body said
    // the row could not be loaded.
    enabled: false,
    select: (raw) => ({ description: describeItem(address, toItemDetail(raw)) }),
  });

  if (isPending) {
    return <SkeletonText text="product-p" lines={['100px']} />;
  }

  return data == null ? null : <Paragraph variant="secondary">{data.description}</Paragraph>;
}
